import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DndContext, useDraggable, useDroppable, DragOverlay, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { Box, Card, CardContent, Typography, Grid, Paper, IconButton, useTheme, alpha, Slider, Button, Collapse, Snackbar, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TvIcon from '@mui/icons-material/Tv';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import GridOnIcon from '@mui/icons-material/GridOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import RouterIcon from '@mui/icons-material/Router';
import WifiIcon from '@mui/icons-material/Wifi';
import { getApparatusIcon } from '../icons/ApparatusIcons';

const PIXELS_PER_METER = 15;

const getEquipmentTypes = (theme, competitionColor) => {
  // Use competition color if available, otherwise fallback to theme primary
  const primaryColor = competitionColor || theme.palette.primary.main;

  // Helper to create standard parts
  const createMat = (w, h, color) => ({ type: 'rect', w, h, color: color || primaryColor, opacity: 0.5 });
  const createBody = (w, h, color = '#555') => ({ type: 'rect', w, h, color, opacity: 1 });
  const createLine = (w, h, color = '#fff') => ({ type: 'rect', w, h, color, opacity: 0.9 });

  return [
    // Apparatus
    { 
      id: 'floor', 
      label: 'Floor Exercise',
      category: 'Apparatus',
      width: 14 * PIXELS_PER_METER, 
      height: 14 * PIXELS_PER_METER, 
      color: primaryColor,
      parts: [
        { ...createMat(14 * PIXELS_PER_METER, 14 * PIXELS_PER_METER, primaryColor), x: 0, y: 0 }, // Safety border / full mat
        { ...createMat(12 * PIXELS_PER_METER, 12 * PIXELS_PER_METER, alpha(primaryColor, 0.2)), x: 1 * PIXELS_PER_METER, y: 1 * PIXELS_PER_METER }, // Performance area
        { ...createLine(12 * PIXELS_PER_METER, 2, '#fff'), x: 1 * PIXELS_PER_METER, y: 1 * PIXELS_PER_METER }, // Border lines
        { ...createLine(12 * PIXELS_PER_METER, 2, '#fff'), x: 1 * PIXELS_PER_METER, y: 13 * PIXELS_PER_METER },
        { ...createLine(2, 12 * PIXELS_PER_METER, '#fff'), x: 1 * PIXELS_PER_METER, y: 1 * PIXELS_PER_METER },
        { ...createLine(2, 12 * PIXELS_PER_METER, '#fff'), x: 13 * PIXELS_PER_METER, y: 1 * PIXELS_PER_METER },
      ]
    },
    { 
      id: 'vault', 
      label: 'Vault',
      category: 'Apparatus',
      width: 32.2 * PIXELS_PER_METER, // Total: 25m runway + 1.2m board + 6m landing
      height: 3 * PIXELS_PER_METER, // 3m width (landing zone)
      color: primaryColor,
      parts: [
        // Runway: 25m × 1m (FIG: ≥25m, max 28m)
        { ...createMat(25 * PIXELS_PER_METER, 1 * PIXELS_PER_METER, '#B0BEC5'), x: 0, y: 1 * PIXELS_PER_METER },
        
        // Springboard: 1.2m × 0.6m (FIG standard)
        { ...createBody(1.2 * PIXELS_PER_METER, 0.6 * PIXELS_PER_METER, '#EF5350'), x: 24.8 * PIXELS_PER_METER, y: 1.2 * PIXELS_PER_METER },

        // Table Base (pedestal)
        { ...createBody(0.4 * PIXELS_PER_METER, 0.4 * PIXELS_PER_METER, '#555'), x: 25.8 * PIXELS_PER_METER, y: 1.3 * PIXELS_PER_METER },

        // Table Top: 1.2m × 0.95m (FIG standard)
        { ...createBody(1.2 * PIXELS_PER_METER, 0.95 * PIXELS_PER_METER, '#D7CCC8'), x: 25.4 * PIXELS_PER_METER, y: 1.025 * PIXELS_PER_METER }, 
        
        // Landing Mat: 6m × 3m (FIG standard)
        { ...createMat(6 * PIXELS_PER_METER, 3 * PIXELS_PER_METER, primaryColor), x: 26.2 * PIXELS_PER_METER, y: 0 } 
      ]
    },
    { 
      id: 'bars', 
      label: 'Uneven Bars',
      category: 'Apparatus',
      width: 6 * PIXELS_PER_METER, 
      height: 12 * PIXELS_PER_METER, // FIG standard: 6m × 12m working zone
      color: primaryColor,
      parts: [
        // Main Mat: 6m × 12m (FIG standard)
        { ...createMat(6 * PIXELS_PER_METER, 12 * PIXELS_PER_METER, primaryColor), x: 0, y: 0 }, 

        // Vertical Uprights/Frame (centered)
        { ...createBody(0.1 * PIXELS_PER_METER, 3 * PIXELS_PER_METER, '#555'), x: 1.8 * PIXELS_PER_METER, y: 4.5 * PIXELS_PER_METER }, // Left Upright
        { ...createBody(0.1 * PIXELS_PER_METER, 3 * PIXELS_PER_METER, '#555'), x: 4.2 * PIXELS_PER_METER, y: 4.5 * PIXELS_PER_METER }, // Right Upright

        // Horizontal Bars (2.4m rail length - FIG standard)
        { ...createBody(2.4 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#fff'), x: 1.8 * PIXELS_PER_METER, y: 5 * PIXELS_PER_METER }, // Low Bar
        { ...createBody(2.4 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#fff'), x: 1.8 * PIXELS_PER_METER, y: 6.5 * PIXELS_PER_METER }, // High Bar
      ]
    },
    { 
      id: 'beam', 
      label: 'Balance Beam',
      category: 'Apparatus',
      width: 4 * PIXELS_PER_METER, 
      height: 18 * PIXELS_PER_METER, // FIG standard: 4m × 18m total matted area
      color: primaryColor,
      parts: [
        // Mat: 4m × 18m (FIG standard)
        { ...createMat(4 * PIXELS_PER_METER, 18 * PIXELS_PER_METER, primaryColor), x: 0, y: 0 }, 
        // Beam: 5m × 0.1m (10cm wide)
        { ...createBody(0.1 * PIXELS_PER_METER, 5 * PIXELS_PER_METER, '#D7CCC8'), x: 1.95 * PIXELS_PER_METER, y: 6.5 * PIXELS_PER_METER }, 
        // Legs (centered on beam)
        { ...createBody(0.4 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#555'), x: 1.8 * PIXELS_PER_METER, y: 7.5 * PIXELS_PER_METER }, 
        { ...createBody(0.4 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#555'), x: 1.8 * PIXELS_PER_METER, y: 10.5 * PIXELS_PER_METER }, 
      ]
    },
    { 
      id: 'pommel', 
      label: 'Pommel Horse',
      category: 'Apparatus',
      width: 4 * PIXELS_PER_METER, 
      height: 4 * PIXELS_PER_METER, 
      color: primaryColor,
      parts: [
        // Mat: 4m x 4m
        { ...createMat(4 * PIXELS_PER_METER, 4 * PIXELS_PER_METER, primaryColor), x: 0, y: 0 },
        // Horse: 1.6m x 0.35m
        { ...createBody(0.35 * PIXELS_PER_METER, 1.6 * PIXELS_PER_METER, '#D7CCC8'), x: 1.825 * PIXELS_PER_METER, y: 1.2 * PIXELS_PER_METER } 
      ]
    },
    { 
      id: 'rings', 
      label: 'Still Rings',
      category: 'Apparatus',
      width: 2.4 * PIXELS_PER_METER, 
      height: 5.2 * PIXELS_PER_METER, // FIG standard: 8' × 17' (2.4m × 5.2m)
      color: primaryColor,
      parts: [
        // Landing mat: 8' × 17' (2.4m × 5.2m) - FIG standard
        { ...createMat(2.4 * PIXELS_PER_METER, 5.2 * PIXELS_PER_METER, primaryColor), x: 0, y: 0 },
        // Frame/cable structure top bar (centered)
        { ...createBody(2 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#555'), x: 0.2 * PIXELS_PER_METER, y: 2.4 * PIXELS_PER_METER }, // Top bar
        // Rings cables (50cm apart, centered)
        { ...createBody(0.1 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#fff'), x: 0.95 * PIXELS_PER_METER, y: 2.4 * PIXELS_PER_METER },
        { ...createBody(0.1 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#fff'), x: 1.35 * PIXELS_PER_METER, y: 2.4 * PIXELS_PER_METER },
      ]
    },
    { 
      id: 'pbars', 
      label: 'Parallel Bars',
      category: 'Apparatus',
      width: 4 * PIXELS_PER_METER, 
      height: 12 * PIXELS_PER_METER, // FIG standard: 4m × 12m working zone
      color: primaryColor,
      parts: [
        // Mat: 4m × 12m (FIG standard competition configuration)
        { ...createMat(4 * PIXELS_PER_METER, 12 * PIXELS_PER_METER, primaryColor), x: 0, y: 0 },
        // Bars: 3.5m long (FIG standard), centered
        { ...createBody(0.1 * PIXELS_PER_METER, 3.5 * PIXELS_PER_METER, '#D7CCC8'), x: 1.45 * PIXELS_PER_METER, y: 4.25 * PIXELS_PER_METER }, // Left Bar
        { ...createBody(0.1 * PIXELS_PER_METER, 3.5 * PIXELS_PER_METER, '#D7CCC8'), x: 2.45 * PIXELS_PER_METER, y: 4.25 * PIXELS_PER_METER }, // Right Bar
      ]
    },
    { 
      id: 'highbar', 
      label: 'High Bar',
      category: 'Apparatus',
      width: 4 * PIXELS_PER_METER, 
      height: 12 * PIXELS_PER_METER, // FIG standard: 4m × 12m working zone
      color: primaryColor,
      parts: [
        // Mat: 4m × 12m (FIG standard)
        { ...createMat(4 * PIXELS_PER_METER, 12 * PIXELS_PER_METER, primaryColor), x: 0, y: 0 },
        // Bar: 2.4m long (FIG standard)
        { ...createBody(2.4 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#fff'), x: 0.8 * PIXELS_PER_METER, y: 6 * PIXELS_PER_METER }, 
        // Uprights (centered)
        { ...createBody(0.2 * PIXELS_PER_METER, 0.2 * PIXELS_PER_METER, '#555'), x: 0.7 * PIXELS_PER_METER, y: 6 * PIXELS_PER_METER },
        { ...createBody(0.2 * PIXELS_PER_METER, 0.2 * PIXELS_PER_METER, '#555'), x: 3.1 * PIXELS_PER_METER, y: 6 * PIXELS_PER_METER },
      ]
    },
    // Scoring
    { 
      id: 'judge', 
      label: 'Judge Table',
      category: 'Scoring',
      width: 3 * PIXELS_PER_METER, 
      height: 1.5 * PIXELS_PER_METER, 
      color: '#333',
      parts: [
        { ...createBody(3 * PIXELS_PER_METER, 1 * PIXELS_PER_METER, '#333'), x: 0, y: 0.25 * PIXELS_PER_METER },
        { ...createMat(3 * PIXELS_PER_METER, 0.5 * PIXELS_PER_METER, '#fff', 0.2), x: 0, y: 0.5 * PIXELS_PER_METER } // Papers/Surface
      ]
    },
    {
      id: 'scoring_table',
      label: 'Scoring Table',
      category: 'Scoring',
      width: 9 * PIXELS_PER_METER, // 3x judge table length (3m × 3 = 9m)
      height: 1.5 * PIXELS_PER_METER, // Same height as judge table
      color: '#424242',
      parts: [
        { ...createBody(9 * PIXELS_PER_METER, 1 * PIXELS_PER_METER, '#424242'), x: 0, y: 0.25 * PIXELS_PER_METER },
        { ...createMat(9 * PIXELS_PER_METER, 0.5 * PIXELS_PER_METER, '#fff', 0.2), x: 0, y: 0.5 * PIXELS_PER_METER } // Papers/Surface
      ]
    },
    // Display
    {
      id: 'centerhung',
      label: 'Centerhung Display',
      category: 'Display',
      width: 4 * PIXELS_PER_METER,
      height: 4 * PIXELS_PER_METER,
      color: '#000',
      parts: [
        { ...createBody(4 * PIXELS_PER_METER, 4 * PIXELS_PER_METER, '#000'), x: 0, y: 0 },
        { ...createMat(3 * PIXELS_PER_METER, 3 * PIXELS_PER_METER, '#2196F3', 0.3), x: 0.5 * PIXELS_PER_METER, y: 0.5 * PIXELS_PER_METER } // Screen glow
      ]
    },
    {
      id: 'corner_board',
      label: 'Corner Board',
      category: 'Display',
      width: 3 * PIXELS_PER_METER,
      height: 0.5 * PIXELS_PER_METER,
      color: '#000',
      parts: [
        { ...createBody(3 * PIXELS_PER_METER, 0.5 * PIXELS_PER_METER, '#000'), x: 0, y: 0 },
        { ...createMat(2.8 * PIXELS_PER_METER, 0.3 * PIXELS_PER_METER, '#2196F3', 0.3), x: 0.1 * PIXELS_PER_METER, y: 0.1 * PIXELS_PER_METER }
      ]
    },
    {
      id: 'tri_display',
      label: 'Tri Display',
      category: 'Display',
      width: 2.25 * PIXELS_PER_METER, // 25% smaller
      height: 2.25 * PIXELS_PER_METER,
      color: '#000',
      parts: [
        // Equilateral triangle with rounded corners (using quadratic bezier curves)
        // Points: top center (50, 13.4), bottom-left (6.7, 86.6), bottom-right (93.3, 86.6)
        // Rounded using Q (quadratic) curves at each corner
        { 
          type: 'svg', 
          path: 'M 50 16.4 Q 50 13.4 52 14.9 L 90.3 84.1 Q 93.3 88.6 88.3 88.6 L 11.7 88.6 Q 6.7 88.6 9.7 84.1 L 48 14.9 Q 50 13.4 50 16.4 Z',
          w: 2.25 * PIXELS_PER_METER, 
          h: 2.25 * PIXELS_PER_METER, 
          color: '#000', 
          opacity: 1 
        },
        { 
          type: 'svg', 
          path: 'M 50 22 Q 50 20 51.5 21 L 85 80 Q 87 83 83 83 L 17 83 Q 13 83 15 80 L 48.5 21 Q 50 20 50 22 Z',
          w: 2.25 * PIXELS_PER_METER, 
          h: 2.25 * PIXELS_PER_METER, 
          color: '#2196F3', 
          opacity: 0.3 
        } // Glow
      ]
    },
    {
      id: 'side_display',
      label: 'Side Display',
      category: 'Display',
      width: 4 * PIXELS_PER_METER,
      height: 0.6 * PIXELS_PER_METER, // Thicker than corner board (0.5m)
      color: '#000',
      parts: [
        { ...createBody(4 * PIXELS_PER_METER, 0.6 * PIXELS_PER_METER, '#000'), x: 0, y: 0 },
        { ...createMat(3.8 * PIXELS_PER_METER, 0.4 * PIXELS_PER_METER, '#2196F3', 0.3), x: 0.1 * PIXELS_PER_METER, y: 0.1 * PIXELS_PER_METER } // Blue screen
      ]
    },
    {
      id: 'display_array',
      label: 'Display Array',
      category: 'Display',
      width: 6 * PIXELS_PER_METER,
      height: 1 * PIXELS_PER_METER,
      color: '#333',
      parts: [
        { ...createBody(6 * PIXELS_PER_METER, 1 * PIXELS_PER_METER, '#222'), x: 0, y: 0 },
        // 3 screens
        { ...createMat(1.8 * PIXELS_PER_METER, 0.8 * PIXELS_PER_METER, '#2196F3', 0.3), x: 0.1 * PIXELS_PER_METER, y: 0.1 * PIXELS_PER_METER },
        { ...createMat(1.8 * PIXELS_PER_METER, 0.8 * PIXELS_PER_METER, '#2196F3', 0.3), x: 2.1 * PIXELS_PER_METER, y: 0.1 * PIXELS_PER_METER },
        { ...createMat(1.8 * PIXELS_PER_METER, 0.8 * PIXELS_PER_METER, '#2196F3', 0.3), x: 4.1 * PIXELS_PER_METER, y: 0.1 * PIXELS_PER_METER }
      ]
    },
    // Production
    {
      id: 'camera',
      label: 'Camera',
      category: 'Production',
      width: 2 * PIXELS_PER_METER,
      height: 2 * PIXELS_PER_METER,
      color: '#333',
      parts: [
        // Camera body - larger and easier to grab
        { ...createBody(1 * PIXELS_PER_METER, 0.8 * PIXELS_PER_METER, '#333'), x: 0.5 * PIXELS_PER_METER, y: 0.6 * PIXELS_PER_METER },
        // Lens
        { ...createBody(0.3 * PIXELS_PER_METER, 0.3 * PIXELS_PER_METER, '#fff'), x: 0.85 * PIXELS_PER_METER, y: 0.4 * PIXELS_PER_METER },
        // FOV Cone - very light, almost invisible
        { type: 'fov', w: 2 * PIXELS_PER_METER, h: 2 * PIXELS_PER_METER, color: 'rgba(255, 235, 59, 0.05)', x: 0, y: 0 } 
      ]
    },
    // Network
    {
      id: 'ethernet',
      label: 'Ethernet Switch',
      category: 'Network',
      width: 1.5 * PIXELS_PER_METER,
      height: 1 * PIXELS_PER_METER,
      color: '#607D8B',
      parts: [
        // Switch body
        { ...createBody(1.5 * PIXELS_PER_METER, 0.6 * PIXELS_PER_METER, '#607D8B'), x: 0, y: 0.2 * PIXELS_PER_METER },
        // Ports indication (small rectangles)
        { ...createBody(0.2 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#4CAF50'), x: 0.2 * PIXELS_PER_METER, y: 0.35 * PIXELS_PER_METER },
        { ...createBody(0.2 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#4CAF50'), x: 0.5 * PIXELS_PER_METER, y: 0.35 * PIXELS_PER_METER },
        { ...createBody(0.2 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#4CAF50'), x: 0.8 * PIXELS_PER_METER, y: 0.35 * PIXELS_PER_METER },
        { ...createBody(0.2 * PIXELS_PER_METER, 0.1 * PIXELS_PER_METER, '#4CAF50'), x: 1.1 * PIXELS_PER_METER, y: 0.35 * PIXELS_PER_METER }
      ]
    },
    {
      id: 'wifi',
      label: 'WiFi Access Point',
      category: 'Network',
      width: 1 * PIXELS_PER_METER,
      height: 1 * PIXELS_PER_METER,
      color: '#2196F3',
      parts: [
        // AP body (circular-ish)
        { ...createBody(0.6 * PIXELS_PER_METER, 0.6 * PIXELS_PER_METER, '#fff'), x: 0.2 * PIXELS_PER_METER, y: 0.2 * PIXELS_PER_METER },
        // Center indicator
        { ...createBody(0.2 * PIXELS_PER_METER, 0.2 * PIXELS_PER_METER, '#2196F3'), x: 0.4 * PIXELS_PER_METER, y: 0.4 * PIXELS_PER_METER }
      ]
    }
  ];
};

const DraggableSource = ({ type, onAdd, competitionColor, count }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `source-${type.id}`,
    data: { type, source: 'toolbox' }
  });

  // Map equipment type IDs to apparatus abbreviations and icons
  const apparatusMap = {
    'floor': 'FX',
    'vault': 'VT',
    'bars': 'UB',
    'beam': 'BB',
    'pommel': 'PH',
    'rings': 'SR',
    'pbars': 'PB',
    'highbar': 'HB'
  };

  const iconMap = {
    'judge': TableRestaurantIcon,
    'scoring_table': AssessmentIcon,
    'centerhung': ViewInArIcon,
    'corner_board': TvIcon,
    'tri_display': ChangeHistoryIcon,
    'side_display': HorizontalRuleIcon,
    'display_array': GridOnIcon,
    'camera': VideocamIcon,
    'ethernet': RouterIcon,
    'wifi': WifiIcon
  };

  const apparatusAbbrev = apparatusMap[type.id];
  const ApparatusIcon = apparatusAbbrev ? getApparatusIcon(apparatusAbbrev, { primary: competitionColor }) : null;
  const MuiIcon = iconMap[type.id];

  // Category-specific styling
  const getIconStyle = () => {
    if (type.category === 'Scoring') {
      return { bgcolor: '#fff', iconColor: '#000' };
    } else if (type.category === 'Display') {
      return { bgcolor: '#2196F3', iconColor: '#000' };
    } else if (type.category === 'Production') {
      return { bgcolor: '#000', iconColor: '#FFC107' };
    } else if (type.category === 'Network') {
      return { bgcolor: '#607D8B', iconColor: '#fff' };
    }
    return { bgcolor: 'action.hover', iconColor: type.color };
  };

  const iconStyle = getIconStyle();

  return (
    <Paper
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      elevation={2}
      data-ui={`venue-component-${type.id}`}
      sx={{
        py: 0.5,
        px: 1,
        mb: 0.5,
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {ApparatusIcon ? (
          <ApparatusIcon sx={{ width: 32, height: 32, flexShrink: 0 }} />
        ) : (
          <Box
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: iconStyle.bgcolor,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              flexShrink: 0
            }}
          >
            {MuiIcon ? (
              <MuiIcon sx={{ fontSize: 20, color: iconStyle.iconColor, flexShrink: 0 }} />
            ) : (
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  bgcolor: type.color,
                  borderRadius: 0.5
                }}
              />
            )}
          </Box>
        )}
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {type.label}{count > 0 && ` (${count})`}
        </Typography>
      </Box>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onAdd(type);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        data-ui={`venue-add-${type.id}-button`}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};

const DraggableItem = ({ item, typeDef, isSelected, onSelect, onRotate, onDelete, onPropChange, dragDelta }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    data: { item, source: 'canvas' }
  });

  const widthVal = typeDef.width;
  const heightVal = typeDef.height;

  let finalTransform = transform ? 
    `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined;

  if (!transform && isSelected && dragDelta) {
     finalTransform = `translate3d(${dragDelta.x}px, ${dragDelta.y}px, 0)`;
  }

  const style = finalTransform ? { transform: finalTransform } : undefined;

  const maxDim = Math.max(widthVal, heightVal);
  const controlOffset = maxDim / 2 + 20;

  return (
    <Box
      ref={setNodeRef}
      style={{
        position: 'absolute',
        left: item.x,
        top: item.y,
        width: widthVal,
        height: heightVal,
        zIndex: isDragging ? 100 : (isSelected ? 10 : 1),
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'none',
        ...style
      }}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          onSelect(item.id, true); 
        } else {
          onSelect(item.id, false);
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          cursor: 'move',
          transform: `rotate(${item.rotation || 0}deg)`,
          transformOrigin: 'center center',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          outline: isSelected ? '2px solid #2196F3' : 'none',
          outlineOffset: '2px',
          borderRadius: 1
        }}
      >
          {typeDef.parts.map((part, i) => {
            if (part.type === 'svg') {
               return (
                 <svg key={i} viewBox={`0 0 ${widthVal} ${heightVal}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                    <path d={part.path.replace(/(\d+)/g, (m) => m * (widthVal/100))} fill={part.color} opacity={part.opacity} />
                 </svg>
               );
            } else if (part.type === 'fov') {
               // Render FOV cone if it's a camera
               const fov = item.fov || 60;
               // Simple triangle pointing up/out. 
               // We'll draw a large triangle representing the field of view.
               // 1000px distance.
               const r = 1000;
               const tan = Math.tan((fov/2) * Math.PI / 180);
               const halfW = r * tan;
               // Path from center-bottom (lens) outwards
               // Assume lens is at center for simplicity
               const centerX = widthVal / 2;
               const centerY = heightVal / 2;
               return (
                 <Box
                   key={i}
                   sx={{
                     position: 'absolute',
                     top: centerY,
                     left: centerX,
                     width: 0,
                     height: 0,
                     overflow: 'visible',
                     pointerEvents: 'none',
                     zIndex: -1
                   }}
                 >
                    <svg width="2000" height="2000" viewBox="0 0 2000 2000" style={{ position: 'absolute', top: -1000, left: -1000, transform: 'rotate(-90deg)' }}>
                       <path d={`M 1000 1000 L ${1000 + r} ${1000 - halfW} L ${1000 + r} ${1000 + halfW} Z`} fill={part.color} />
                    </svg>
                 </Box>
               );
            }
            return (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: part.x,
                  top: part.y,
                  width: part.w,
                  height: part.h,
                  bgcolor: part.color,
                  opacity: part.opacity || 1,
                  borderRadius: 1,
                  border: '1px solid rgba(0,0,0,0.1)'
                }}
              />
            );
          })}
      </Box>

      {/* Controls (outside rotated box so they don't rotate) */}
      <Box
        className="controls"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) translateY(-${controlOffset}px)`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 0.2s',
          zIndex: 10,
          pointerEvents: isSelected ? 'auto' : 'none',
        }}
      >
           <Paper sx={{ p: 0.5, display: 'flex', gap: 1, borderRadius: 4 }}>
             <IconButton
               size="small"
               onClick={(e) => {
                 e.stopPropagation();
                 onRotate(item.id);
               }}
               data-ui="venue-item-rotate-button"
             >
               <RotateRightIcon fontSize="small" />
             </IconButton>
             <IconButton
               size="small"
               onPointerDown={(e) => { e.stopPropagation(); onDelete(item.id); }}
               sx={{ color: 'error.main' }}
               data-ui="venue-item-delete-button"
             >
               <DeleteIcon fontSize="small" />
             </IconButton>
           </Paper>

           {/* Camera FOV Control */}
           {item.type?.id === 'camera' && (
             <Paper sx={{ p: 1, width: 120, borderRadius: 2 }}>
               <Typography variant="caption" display="block" align="center">Field of View</Typography>
               <Slider
                 size="small"
                 value={item.fov || 60}
                 min={10}
                 max={120}
                 onChange={(e, val) => onPropChange(item.id, 'fov', val)}
                 onPointerDown={(e) => e.stopPropagation()} // Prevent drag
                 data-ui="venue-camera-fov-slider"
               />
             </Paper>
           )}
      </Box>
    </Box>
  );
};

const Canvas = ({ items, equipmentTypes, selectedIds, onSelect, onMultiSelect, onRotate, onDelete, onPropChange, dragDelta }) => {
  const { setNodeRef } = useDroppable({
    id: 'canvas',
  });
  const theme = useTheme();
  const canvasRef = useRef(null);
  const [selectionBox, setSelectionBox] = useState(null);

  const handlePointerDown = (e) => {
    if (e.target === canvasRef.current || e.target === canvasRef.current?.parentElement) {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setSelectionBox({ startX: x, startY: y, currentX: x, currentY: y, isSelecting: true });
        onSelect(null); 
    }
  };

  const handlePointerMove = (e) => {
    if (selectionBox?.isSelecting) {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setSelectionBox(prev => ({ ...prev, currentX: x, currentY: y }));
    }
  };

  const handlePointerUp = (e) => {
    if (selectionBox?.isSelecting) {
        const x1 = Math.min(selectionBox.startX, selectionBox.currentX);
        const x2 = Math.max(selectionBox.startX, selectionBox.currentX);
        const y1 = Math.min(selectionBox.startY, selectionBox.currentY);
        const y2 = Math.max(selectionBox.startY, selectionBox.currentY);

        const newSelectedIds = items.filter(item => {
            if (!item.type && !item.typeId) return false;
            const typeDef = equipmentTypes.find(t => t.id === (item.type?.id || item.typeId)) || item.type;
            if (!typeDef) return false;
            // Use center point for simplified hit test in box selection for now, or AABB
            // Let's use item center
            const cx = item.x + typeDef.width/2;
            const cy = item.y + typeDef.height/2;
            return cx > x1 && cx < x2 && cy > y1 && cy < y2;
        }).map(i => i.id);

        if (newSelectedIds.length > 0) {
            onMultiSelect(newSelectedIds);
        }
        setSelectionBox(null);
    }
  };

  const setRefs = (node) => {
      setNodeRef(node);
      canvasRef.current = node;
  };

  return (
    <Box
      ref={setRefs}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      data-ui="venue-layout-canvas"
      sx={{
        width: '100%',
        height: 600,
        bgcolor: theme.palette.background.default,
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundImage: `radial-gradient(${theme.palette.mode === 'dark' ? '#444' : '#ddd'} 1px, transparent 1px)`,
        backgroundSize: '20px 20px',
        touchAction: 'none'
      }}
    >
      {items.map(item => {
        if (!item.type && !item.typeId) return null;
        const typeDef = equipmentTypes.find(t => t.id === (item.type?.id || item.typeId)) || item.type;
        if (!typeDef) return null;
        return (
          <DraggableItem 
            key={item.id} 
            item={item} 
            typeDef={typeDef}
            isSelected={selectedIds.has(item.id)}
            dragDelta={selectedIds.has(item.id) ? dragDelta : null} 
            onSelect={onSelect}
            onRotate={onRotate}
            onDelete={onDelete}
            onPropChange={onPropChange}
          />
        );
      })}
      {selectionBox && (
          <Box
            sx={{
                position: 'absolute',
                left: Math.min(selectionBox.startX, selectionBox.currentX),
                top: Math.min(selectionBox.startY, selectionBox.currentY),
                width: Math.abs(selectionBox.currentX - selectionBox.startX),
                height: Math.abs(selectionBox.currentY - selectionBox.startY),
                border: '1px dashed #2196F3',
                bgcolor: 'rgba(33, 150, 243, 0.1)',
                pointerEvents: 'none',
                zIndex: 1000
            }}
          />
      )}
    </Box>
  );
};

const FacilityLayoutDesigner = ({ initialItems, onLayoutChange, onSave }) => {
  const [items, setItems] = useState(initialItems || []);
  const [savedState, setSavedState] = useState(initialItems || []);
  const [history, setHistory] = useState([initialItems || []]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeDragItem, setActiveDragItem] = useState(null);
  const [dragDelta, setDragDelta] = useState(null);
  const [clipboard, setClipboard] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedCategories, setExpandedCategories] = useState({
    'Apparatus': true,
    'Scoring': true,
    'Display': true,
    'Production': true,
    'Network': true
  });
  
  const currentCompetition = useSelector(state => state.competition.currentCompetition);
  const competitionData = currentCompetition?.competition || currentCompetition;
  const competitionColor = competitionData?.colors?.primary;

  const theme = useTheme();
  const equipmentTypes = useMemo(() => getEquipmentTypes(theme, competitionColor), [theme, competitionColor]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Sync with initialItems when prop changes (e.g., loading from Redux)
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      // Need to rehydrate type objects from equipment types
      const rehydratedItems = initialItems.map(item => {
        const typeDef = equipmentTypes.find(t => t.id === (item.type?.id || item.typeId));
        if (typeDef) {
          return { ...item, type: typeDef };
        }
        return item;
      });
      setItems(rehydratedItems);
      setSavedState(rehydratedItems);
      setHistory([rehydratedItems]);
      setHistoryIndex(0);
    }
  }, [initialItems, equipmentTypes]);

  // Helper to add to history
  const addToHistory = (newItems) => {
    // Remove any history after current index (when undoing then making new changes)
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newItems);
    // Keep only last 50 states to avoid memory issues
    if (newHistory.length > 50) {
      newHistory.shift();
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistoryIndex(newHistory.length - 1);
    }
    setHistory(newHistory);
  };

  // Helper to notify parent of changes (strips heavy type objects)
  const notifyLayoutChange = (updatedItems) => {
    if (onLayoutChange) {
      const lightItems = updatedItems.map(item => {
        const { type, ...rest } = item;
        return {
          ...rest,
          typeId: type?.id || item.typeId
        };
      });
      onLayoutChange(lightItems);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedIds.size > 0) {
          e.preventDefault();
          setItems(prev => prev.filter(item => !selectedIds.has(item.id)));
          setSelectedIds(new Set());
        }
      }

      // Don't handle 'r' key with Cmd/Ctrl modifier to avoid blocking browser refresh
      if (e.key.toLowerCase() === 'r' && !e.ctrlKey && !e.metaKey) {
        if (selectedIds.size > 0) {
          e.preventDefault();
          const rotationAmount = e.shiftKey ? -15 : 15; // Shift+R = counter-clockwise
          setItems(prev => prev.map(item => {
            if (selectedIds.has(item.id)) {
              return { ...item, rotation: (item.rotation || 0) + rotationAmount };
            }
            return item;
          }));
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectedIds(new Set(items.map(i => i.id)));
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedIds(new Set());
      }

      // Copy (Cmd+C or Ctrl+C)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (selectedIds.size > 0) {
          e.preventDefault();
          const itemsToCopy = items.filter(item => selectedIds.has(item.id));
          setClipboard(itemsToCopy);
          setSnackbar({ open: true, message: `Copied ${itemsToCopy.length} item(s)`, severity: 'info' });
        }
      }

      // Paste (Cmd+V or Ctrl+V)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (clipboard.length > 0) {
          e.preventDefault();
          const pastedItems = clipboard.map(item => ({
            ...item,
            id: `item-${Date.now()}-${Math.random()}`,
            x: item.x + 20, // Offset pasted items slightly
            y: item.y + 20
          }));
          const updatedItems = [...items, ...pastedItems];
          setItems(updatedItems);
          addToHistory(updatedItems);
          setSelectedIds(new Set(pastedItems.map(i => i.id)));
          notifyLayoutChange(updatedItems);
          setSnackbar({ open: true, message: `Pasted ${pastedItems.length} item(s)`, severity: 'success' });
        }
      }

      // Undo (Cmd+Z or Ctrl+Z)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleUndo();
      }

      // Save (Cmd+S or Ctrl+S)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        if (selectedIds.size > 0) {
          e.preventDefault();
          const amount = e.shiftKey ? 10 : 1;
          const dx = e.key === 'ArrowLeft' ? -amount : e.key === 'ArrowRight' ? amount : 0;
          const dy = e.key === 'ArrowUp' ? -amount : e.key === 'ArrowDown' ? amount : 0;

          setItems(prev => prev.map(item => {
            if (selectedIds.has(item.id)) {
              return { ...item, x: item.x + dx, y: item.y + dy };
            }
            return item;
          }));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, items, clipboard]);

  const handleSelect = (id, toggle = false) => {
      if (id === null) {
          setSelectedIds(new Set());
          return;
      }
      setSelectedIds(prev => {
          const newSet = new Set(toggle ? prev : []);
          if (toggle && prev.has(id)) {
              newSet.delete(id);
          } else {
              newSet.add(id);
          }
          return newSet;
      });
  };

  const handleMultiSelect = (ids) => {
      setSelectedIds(new Set(ids));
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const currentItem = active.data.current;
    setActiveDragItem(currentItem);
    
    if (currentItem?.source === 'canvas') {
        if (!selectedIds.has(active.id)) {
            setSelectedIds(new Set([active.id]));
        }
    }
  };

  const handleDragMove = (event) => {
      const { delta, active } = event;
      if (active.data.current?.source === 'canvas') {
          setDragDelta(delta);
      }
  };

  const handleDragEnd = (event) => {
    const { active, over, delta } = event;
    setActiveDragItem(null);
    setDragDelta(null);

    if (!over) return;

    if (active.data.current?.source === 'toolbox') {
      const typeWidth = active.data.current.type.width;
      const typeHeight = active.data.current.type.height;
      
      // Get the final position from the drag overlay
      const finalRect = active.rect.current.translated;
      const canvasRect = over.rect;
      
      // Position item where it was dropped, centered
      const x = finalRect.left + (finalRect.width / 2) - canvasRect.left - (typeWidth / 2);
      const y = finalRect.top + (finalRect.height / 2) - canvasRect.top - (typeHeight / 2);

      const newItem = {
        id: `item-${Date.now()}`,
        type: active.data.current.type,
        x: Math.max(0, x), 
        y: Math.max(0, y),
        rotation: 0
      };
      const updatedItems = [...items, newItem];
      setItems(updatedItems);
      addToHistory(updatedItems);
      setSelectedIds(new Set([newItem.id]));
      notifyLayoutChange(updatedItems); // Notify after adding item
    } else if (active.data.current?.source === 'canvas') {
      setItems(prevItems => {
        const updatedItems = prevItems.map(item => {
          if (selectedIds.has(item.id)) {
            return {
              ...item,
              x: item.x + delta.x,
              y: item.y + delta.y
            };
          }
          return item;
        });
        addToHistory(updatedItems);
        notifyLayoutChange(updatedItems); // Notify after drag ends
        return updatedItems;
      });
    }
  };

  const handleAdd = (type) => {
    const offset = items.length * 20; 
    const newItem = {
      id: `item-${Date.now()}`,
      type: type,
      x: 50 + (offset % 200),
      y: 50 + (offset % 200),
      rotation: 0
    };
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    addToHistory(updatedItems);
    setSelectedIds(new Set([newItem.id]));
    notifyLayoutChange(updatedItems); // Notify after adding via button
  };

  const handleRotate = (id) => {
    const idsToRotate = selectedIds.has(id) ? Array.from(selectedIds) : [id];
                                                                          
    setItems(prev => {                                   
      const updatedItems = prev.map(item => {
        if (idsToRotate.includes(item.id)) { 
          return { ...item, rotation: (item.rotation || 0) + 15 }; // 15 deg increments
        }                                                                   
        return item;
      });
      addToHistory(updatedItems);
      notifyLayoutChange(updatedItems); // Notify after rotation
      return updatedItems;
    });                                                                  
  };

  const handlePropChange = (id, prop, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, [prop]: value };
      }
      return item;
    }));
  };

  const handleDelete = (id) => {
      const idsToDelete = selectedIds.has(id) ? selectedIds : new Set([id]);
      setItems(prev => {
        const updatedItems = prev.filter(item => !idsToDelete.has(item.id));
        addToHistory(updatedItems);
        notifyLayoutChange(updatedItems); // Notify after deletion
        return updatedItems;
      });
      setSelectedIds(new Set());
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setItems(history[newIndex]);
      notifyLayoutChange(history[newIndex]);
      setSelectedIds(new Set());
      setSnackbar({ open: true, message: 'Action undone', severity: 'info' });
    }
  };

  const handleReset = () => {
    setItems(savedState);
    addToHistory(savedState);
    notifyLayoutChange(savedState);
    setSelectedIds(new Set());
    setSnackbar({ open: true, message: 'Layout reset to last saved state', severity: 'info' });
  };

  const handleSave = () => {
    setSavedState([...items]);
    setSnackbar({ open: true, message: 'Layout saved locally - click main Save to persist', severity: 'success' });
    if (onSave) {
      onSave();
    }
  };

  const handleCopy = () => {
    if (selectedIds.size > 0) {
      const itemsToCopy = items.filter(item => selectedIds.has(item.id));
      setClipboard(itemsToCopy);
      setSnackbar({ open: true, message: `Copied ${itemsToCopy.length} item(s)`, severity: 'info' });
    }
  };

  const handlePaste = () => {
    if (clipboard.length > 0) {
      const pastedItems = clipboard.map(item => ({
        ...item,
        id: `item-${Date.now()}-${Math.random()}`,
        x: item.x + 20, // Offset pasted items slightly
        y: item.y + 20
      }));
      const updatedItems = [...items, ...pastedItems];
      setItems(updatedItems);
      addToHistory(updatedItems);
      setSelectedIds(new Set(pastedItems.map(i => i.id)));
      notifyLayoutChange(updatedItems);
      setSnackbar({ open: true, message: `Pasted ${pastedItems.length} item(s)`, severity: 'success' });
    }
  };

  const handleToggleAll = () => {
    const allExpanded = Object.values(expandedCategories).every(v => v);
    const newState = !allExpanded;
    setExpandedCategories({
      'Apparatus': newState,
      'Scoring': newState,
      'Display': newState,
      'Production': newState,
      'Network': newState
    });
  };

  return (
    <DndContext 
      sensors={sensors} 
      onDragStart={handleDragStart} 
      onDragMove={handleDragMove} 
      onDragEnd={handleDragEnd}
      autoScroll={false}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          width: '100%',
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
          <Card variant="outlined" data-ui="venue-components-card">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Components</Typography>
                <IconButton
                  size="small"
                  onClick={handleToggleAll}
                  sx={{ p: 0 }}
                  data-ui="venue-toggle-all-categories-button"
                >
                  {Object.values(expandedCategories).every(v => v) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {['Apparatus', 'Scoring', 'Display', 'Production', 'Network'].map(category => {
                  const categoryItems = equipmentTypes.filter(type => type.category === category);
                  if (categoryItems.length === 0) return null;
                  
                  // Count placed items in this category
                  const categoryCount = items.filter(item => 
                    item.type?.id && categoryItems.some(catItem => catItem.id === item.type.id)
                  ).length;
                  
                  const isExpanded = expandedCategories[category];
                  return (
                    <Box key={category} sx={{ mb: 1 }}>
                      <Box
                        onClick={() => setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                        data-ui={`venue-category-${category.toLowerCase()}-toggle`}
                        sx={{
                          px: 1,
                          py: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                          {category} ({categoryCount})
                        </Typography>
                        <IconButton size="small" sx={{ p: 0 }}>
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                      <Collapse in={isExpanded}>
                        <Box sx={{ mt: 0.5 }}>
                          {categoryItems.map(type => {
                            const typeCount = items.filter(item => item.type?.id === type.id).length;
                            return (
                              <DraggableSource 
                                key={type.id} 
                                type={type} 
                                onAdd={handleAdd} 
                                competitionColor={competitionColor}
                                count={typeCount}
                              />
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card
            variant="outlined"
            data-ui="venue-facility-layout-card"
            sx={{ width: '100%', height: '100%' }}
          >
            <CardContent>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                 <Typography variant="h6">Facility Layout</Typography>
                 <Box sx={{ display: 'flex', gap: 1 }}>
                   <Button
                     variant="outlined"
                     startIcon={<ContentCopyIcon />}
                     onClick={handleCopy}
                     disabled={selectedIds.size === 0}
                     size="small"
                     data-ui="venue-copy-button"
                     sx={{
                       minWidth: 'auto',
                       px: 1.5,
                       py: 0.5,
                       fontSize: '0.75rem',
                       fontWeight: 400,
                       textTransform: 'none',
                       color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                       borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                       '&:hover': {
                         backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                         borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                       },
                     }}
                   >
                     Copy
                   </Button>
                   <Button
                     variant="outlined"
                     startIcon={<ContentPasteIcon />}
                     onClick={handlePaste}
                     disabled={clipboard.length === 0}
                     size="small"
                     data-ui="venue-paste-button"
                     sx={{
                       minWidth: 'auto',
                       px: 1.5,
                       py: 0.5,
                       fontSize: '0.75rem',
                       fontWeight: 400,
                       textTransform: 'none',
                       color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                       borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                       '&:hover': {
                         backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                         borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                       },
                     }}
                   >
                     Paste
                   </Button>
                   <Button
                     variant="outlined"
                     startIcon={<UndoIcon />}
                     onClick={handleUndo}
                     disabled={historyIndex === 0}
                     size="small"
                     data-ui="venue-undo-button"
                     sx={{
                       minWidth: 'auto',
                       px: 1.5,
                       py: 0.5,
                       fontSize: '0.75rem',
                       fontWeight: 400,
                       textTransform: 'none',
                       color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                       borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                       '&:hover': {
                         backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                         borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                       },
                     }}
                   >
                     Undo
                   </Button>
                   <Button
                     variant="outlined"
                     startIcon={<RestartAltIcon />}
                     onClick={handleReset}
                     disabled={JSON.stringify(items) === JSON.stringify(savedState)}
                     size="small"
                     data-ui="venue-reset-button"
                     sx={{
                       minWidth: 'auto',
                       px: 1.5,
                       py: 0.5,
                       fontSize: '0.75rem',
                       fontWeight: 400,
                       textTransform: 'none',
                       color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                       borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                       '&:hover': {
                         backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                         borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                       },
                     }}
                   >
                     Reset
                   </Button>
                   <Button
                     variant="outlined"
                     startIcon={<SaveIcon />}
                     onClick={handleSave}
                     disabled={JSON.stringify(items) === JSON.stringify(savedState)}
                     size="small"
                     data-ui="venue-save-button"
                     sx={{
                       minWidth: 'auto',
                       px: 1.5,
                       py: 0.5,
                       fontSize: '0.75rem',
                       fontWeight: 400,
                       textTransform: 'none',
                       ...(JSON.stringify(items) !== JSON.stringify(savedState) ? {
                         color: 'warning.main',
                         borderColor: 'warning.main',
                         '&:hover': {
                           backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.12)' : 'rgba(237, 108, 2, 0.08)',
                           borderColor: 'warning.dark',
                         },
                       } : {
                         color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                         borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
                         '&:hover': {
                           backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                           borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                         },
                       })
                     }}
                   >
                     Save
                   </Button>
                 </Box>
               </Box>
              <Canvas 
                items={items} 
                equipmentTypes={equipmentTypes}
                selectedIds={selectedIds} 
                onSelect={handleSelect}
                onMultiSelect={handleMultiSelect}
                onRotate={handleRotate} 
                onDelete={handleDelete}
                onPropChange={handlePropChange}
                dragDelta={dragDelta}
              />
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Drag to select multiple. Shift+Click to toggle. Cmd+C/V to copy/paste. R to rotate (Shift+R for reverse). Arrow keys to nudge.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      
      <DragOverlay dropAnimation={null}>
        {activeDragItem && activeDragItem.source === 'toolbox' ? (
          <Box
            sx={{
              width: activeDragItem.type.width,
              height: activeDragItem.type.height,
              position: 'relative',
              opacity: 0.8,
              pointerEvents: 'none'
            }}
          >
            {activeDragItem.type.parts.map((part, i) => {
              if (part.type === 'svg') {
                const widthVal = activeDragItem.type.width;
                const heightVal = activeDragItem.type.height;
                return (
                  <svg key={i} viewBox={`0 0 ${widthVal} ${heightVal}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                    <path d={part.path.replace(/(\d+)/g, (m) => m * (widthVal/100))} fill={part.color} opacity={part.opacity} />
                  </svg>
                );
              }
              return (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    left: part.x,
                    top: part.y,
                    width: part.w,
                    height: part.h,
                    bgcolor: part.color,
                    opacity: part.opacity || 1,
                    borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                />
              );
            })}
          </Box>
        ) : null}
      </DragOverlay>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DndContext>
  );
};

export default FacilityLayoutDesigner;
